// Route assignments: which driver(s) run each Pilot route on a day, and which stops go to whom.
// Used by both the server (Pilot import) and the dispatch screen, so the rules match.
import { haversineMiles } from './geo.js';

/** Drivers on a route for a day. The day's board wins; otherwise the default from Settings. */
export function driversFor(route, dayRoutes, defaults) {
  if (!route) return [];
  const d = dayRoutes && Object.prototype.hasOwnProperty.call(dayRoutes, route) ? dayRoutes[route] : defaults?.[route];
  return (Array.isArray(d) ? d : d ? [d] : []).filter(Boolean);
}

const bearing = (from, p) => Math.atan2(p.lat - from.lat, (p.lng - from.lng) * Math.cos((from.lat * Math.PI) / 180));

/**
 * Split stops between drivers as pie slices around the lab, so each driver gets one side of town
 * and roughly the same number of stops. Returns Map(stopId -> email).
 */
export function splitStops(stops, emails, lab, pointOf) {
  const out = new Map();
  if (!emails.length) return out;
  const located = stops.filter((s) => pointOf(s));
  const loose = stops.filter((s) => !pointOf(s));
  if (located.length) {
    const ang = located.map((s) => ({ s, a: bearing(lab, pointOf(s)) })).sort((x, y) => x.a - y.a);
    // Start the sweep at the widest empty gap so no driver's slice wraps across town.
    let gapAt = 0; let gap = -1;
    ang.forEach((x, i) => { const next = i + 1 < ang.length ? ang[i + 1].a : ang[0].a + 2 * Math.PI; if (next - x.a > gap) { gap = next - x.a; gapAt = (i + 1) % ang.length; } });
    const ring = [...ang.slice(gapAt), ...ang.slice(0, gapAt)];
    const per = ring.length / emails.length;
    ring.forEach((x, i) => out.set(x.s.id, emails[Math.min(emails.length - 1, Math.floor(i / per))]));
  }
  // No map pin yet: give it to whoever has the fewest stops.
  for (const s of loose) {
    const load = (e) => [...out.values()].filter((v) => v === e).length;
    out.set(s.id, [...emails].sort((a, b) => load(a) - load(b))[0]);
  }
  return out;
}

/**
 * Work out driver changes for one route's stops.
 * Only pending stops move, and stops dispatch moved one at a time (assignSource 'hand') stay put.
 * resplit: true when dispatch changes who drives the route (re-deal everything);
 *          false for new stops arriving later (keep what's there, add new ones to the nearest driver).
 * Returns [{ id, driverEmail }] for stops whose driver should change.
 */
export function assignRoute(stops, emails, lab, pointOf, { resplit = false } = {}) {
  const movable = stops.filter((s) => s.status === 'pending' && s.assignSource !== 'hand');
  const want = new Map();
  if (!emails.length) {
    if (resplit) movable.forEach((s) => want.set(s.id, ''));
  } else if (emails.length === 1) {
    movable.forEach((s) => want.set(s.id, emails[0]));
  } else if (resplit) {
    for (const [id, e] of splitStops(movable, emails, lab, pointOf)) want.set(id, e);
  } else {
    const settled = stops.filter((s) => emails.includes(s.driverEmail));
    for (const s of movable) {
      if (emails.includes(s.driverEmail)) continue;
      const p = pointOf(s);
      let best = null; let bestD = Infinity;
      if (p) for (const o of settled) { const q = pointOf(o); if (!q) continue; const d = haversineMiles(p, q); if (d < bestD) { bestD = d; best = o.driverEmail; } }
      if (!best) { const load = (e) => settled.filter((o) => o.driverEmail === e).length; best = [...emails].sort((a, b) => load(a) - load(b))[0]; }
      want.set(s.id, best);
      settled.push({ ...s, driverEmail: best });
    }
  }
  return stops.filter((s) => want.has(s.id) && want.get(s.id) !== (s.driverEmail || '')).map((s) => ({ id: s.id, driverEmail: want.get(s.id) }));
}
